import { c as _c } from "react/compiler-runtime";
import { IcoSparkles16 } from '@onefootprint/icons';
import type { DataIdentifier } from '@onefootprint/request-types';
import type { ApiNodeDataDocumentExtraction, NodeDataUpdateDocumentExtraction } from '@onefootprint/request-types/dashboard';
import { CodeBlock, Divider, Form } from '@onefootprint/ui';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useDirty from '@/components/playbook-builder/hooks/use-dirty';
import { useGetNodeConfigContext } from '@/components/playbook-builder/hooks/use-get-node-config-context';
import type { ApplyEditMutation } from '../../base-node';
import DocumentIdentifierSelect from './document-identifier-select';
export type DocumentExtractionFormData = {
  name: string;
  documentIdentifier: DataIdentifier;
  schema: string;
  mockData: string;
};
const DocumentExtractionForm = ({
  mutation,
  nodeData,
  nodeId,
  canEdit,
  onSubmit // For storybook testing
}: {
  mutation: ApplyEditMutation;
  nodeData: ApiNodeDataDocumentExtraction;
  nodeId: string;
  canEdit: boolean;
  onSubmit?: (data: ApiNodeDataDocumentExtraction) => void;
}) => {
  const {
    t
  } = useTranslation('playbook-builder', {
    keyPrefix: 'documentExtractionNode'
  });
  const {
    toggle
  } = useDirty();
  const defaultValues: DocumentExtractionFormData = {
    name: nodeData.name ?? t($ => $.nodeName.defaultValue),
    documentIdentifier: nodeData.documentIdentifier as DataIdentifier ?? undefined,
    schema: JSON.stringify(nodeData.schema, null, 2),
    mockData: nodeData.mockData
  };
  const methods = useForm<DocumentExtractionFormData>({
    defaultValues
  });
  const {
    handleSubmit,
    formState: {
      isDirty
    }
  } = methods;
  const config = useGetNodeConfigContext(nodeId, 'document_extraction');
  const documentOptions = config?.documents.map(doc => ({
    label: doc.displayName,
    value: doc.dataIdentifier
  }));
  useEffect(() => {
    toggle('document-review', isDirty);
  }, [isDirty]);
  const handleFormSubmit = async (data: DocumentExtractionFormData) => {
    try {
      const parsedSchema = JSON.parse(data.schema);
      const nodeData_0: NodeDataUpdateDocumentExtraction = {
        kind: 'document_extraction',
        ...data,
        schema: parsedSchema
      };
      onSubmit?.(nodeData_0);
      await mutation.mutateAsync({
        nodeId,
        kind: 'update_node',
        nodeData: nodeData_0
      });
    } catch (_) {}
  };
  return <FormProvider {...methods}>
      <form id="document-review-form" onSubmit={handleSubmit(handleFormSubmit)}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2 w-full md:flex-row md:justify-between md:items-center md:gap-0">
            <div className="text-label-2">{t($_0 => $_0.nodeName.label)}</div>
            <div className="w-full md:w-[50%]">
              <Form.Input<DocumentExtractionFormData> name="name" disabled={!canEdit} className="md:w-[50%]" size="compact" />
            </div>
          </div>
          <Divider />
          <div className="p-3 border border-tertiary rounded-md flex flex-col gap-2">
            <div className="flex items-center gap-1">
              <IcoSparkles16 />
              <span className="text-label-2 text-primary">{t($_1 => $_1.nodeDescription.title)}</span>
            </div>
            <span className="text-body-2 text-secondary">{t($_2 => $_2.nodeDescription.subtitle)}</span>
          </div>
          <div className="flex flex-col gap-8 mb-4">
            <Section title={t($_3 => $_3.configurationSection.title)} subtitle={t($_4 => $_4.configurationSection.subtitle)}>
              <div className="flex flex-col gap-6">
                <DocumentIdentifierSelect documentOptions={documentOptions} canEdit={canEdit} />
                <Form.SchemaEditor<DocumentExtractionFormData> name="schema" disabled={!canEdit} rules={{
                required: t($_5 => $_5.configurationSection.schemaEditor.required)
              }} title={t($_6 => $_6.configurationSection.schemaEditor.title)} subtitle={t($_7 => $_7.configurationSection.schemaEditor.subtitle)} />
              </div>
            </Section>
            <Section title={t($_8 => $_8.mockDataSection.title)} subtitle={t($_9 => $_9.mockDataSection.subtitle)}>
              {canEdit ? <Form.JsonEditor<DocumentExtractionFormData> name="mockData" placeholder={t($_10 => $_10.mockDataSection.placeholder)} rules={{
              required: t($_11 => $_11.mockDataSection.required)
            }} disabled={!canEdit} /> : <CodeBlock language="json" hideHeader>
                  {defaultValues.mockData}
                </CodeBlock>}
            </Section>
          </div>
        </div>
      </form>
    </FormProvider>;
};
const Section = t0 => {
  const $ = _c(10);
  const {
    title,
    subtitle,
    children
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <div className="text-label-2 text-primary">{title}</div>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== subtitle) {
    t2 = <div className="text-body-2 text-tertiary">{subtitle}</div>;
    $[2] = subtitle;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex flex-col gap-1">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== children || $[8] !== t3) {
    t4 = <div className="flex flex-col gap-4">{t3}{children}</div>;
    $[7] = children;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
export default DocumentExtractionForm;